// Tab Financiero — montos, recuperación, top deudores.

import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Skeleton, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, LinearProgress,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { dashEjecFinancieroJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';
const BR = 4;
const fmtMoney = (n) => Number(n || 0).toLocaleString('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 });
const fmtNum = (n) => Number(n || 0).toLocaleString('es-AR');

export default function TabFinanciero() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        dashEjecFinancieroJWT().then(r => { if (!cancel && r?.ok) setData(r.data); }).finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, []);

    if (loading) {
        return (
            <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {[1, 2, 3].map(i => (<Grid key={i} size={{ xs: 12, md: 4 }}><Skeleton variant="rounded" height={140} sx={{ borderRadius: `${BR}px` }} /></Grid>))}
                </Grid>
                <Skeleton variant="rounded" height={400} sx={{ borderRadius: `${BR}px` }} />
            </Box>
        );
    }
    if (!data) return <Alert severity="error">No se pudieron cargar los datos.</Alert>;

    const ratio = Number(data.ratioRecuperacion || 0);
    const res = data.resolucion || {};

    return (
        <Box>
            {/* Cards principales: demandado / planes / recuperado */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            ⚖️ Monto demandado en trámite
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtMoney(data.montoDemandado)}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 1 }}>
                            {fmtNum(data.expedientesConCertificado)} expedientes con certificado
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            💰 En planes de pago
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtMoney(data.montoPlanPago)}</Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 2.5, borderRadius: `${BR}px`,
                        background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', color: 'white' }}>
                        <Typography variant="caption" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            ✅ Recaudado (caja real DGR)
                        </Typography>
                        {data.recuperadoEsGlobal === false ? (
                            <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.9 }}>
                                No disponible por área (el recaudado es provincial).
                            </Typography>
                        ) : (
                            <>
                                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{fmtMoney(data.montoRecuperado)}</Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                                        Cobrado vía planes DGR · tasa s/ demandado: <strong>{ratio.toFixed(1)}%</strong>
                                    </Typography>
                                    <LinearProgress variant="determinate" value={Math.min(ratio, 100)}
                                        sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.3)',
                                            '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
                                </Box>
                                {data.montoResueltas != null && (
                                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 1, fontSize: '0.68rem' }}>
                                        Causas resueltas (demandado, no caja): {fmtMoney(data.montoResueltas)}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Resolución de causas */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px`, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Resolución de causas (judiciales acumuladas)
                </Typography>
                <Grid container spacing={1.5}>
                    {[
                        { label: 'Con plan de pago activo', value: res.conPlanPago, color: '#2e7d32', icon: '💰' },
                        { label: 'Archivados moratoria', value: res.archivadosMoratoria, color: '#0288d1', icon: '📦' },
                        { label: 'Concluidos', value: res.concluidos, color: '#7b1fa2', icon: '✅' },
                        { label: 'Archivados', value: res.archivados, color: '#616161', icon: '🗄️' },
                        { label: 'Cancelados', value: res.cancelados, color: '#c62828', icon: '❌' },
                    ].map((b, i) => (
                        <Grid key={i} size={{ xs: 6, sm: 4, md: 2.4 }}>
                            <Box sx={{ p: 1.25, border: `1px solid ${b.color}33`, borderRadius: `${BR}px`, textAlign: 'center' }}>
                                <Box sx={{ fontSize: 20 }}>{b.icon}</Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: b.color, lineHeight: 1.1 }}>{fmtNum(b.value)}</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666' }}>{b.label}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Monto por módulo + Top áreas por monto */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Monto demandado por módulo
                        </Typography>
                        {(data.montoPorModulo || []).length > 0 ? (
                            <BarChart
                                height={Math.max(280, (data.montoPorModulo || []).length * 28)}
                                layout="horizontal"
                                yAxis={[{ scaleType: 'band', data: (data.montoPorModulo || []).map(m => (m.modulo || '').slice(0, 28)) }]}
                                xAxis={[{ valueFormatter: (v) => `$${(v / 1e6).toFixed(0)}M` }]}
                                series={[{
                                    data: (data.montoPorModulo || []).map(m => Number(m.monto || 0)),
                                    color: '#c62828',
                                    valueFormatter: (v) => fmtMoney(v),
                                }]}
                                margin={{ top: 10, right: 30, bottom: 30, left: 200 }}
                            />
                        ) : <Typography color="text.secondary">Sin datos</Typography>}
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper sx={{ p: 2, borderRadius: `${BR}px`, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                            Top 10 áreas por monto demandado
                        </Typography>
                        <TableContainer sx={{ maxHeight: 380 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Área</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Exp.</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Monto</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(data.topAreasPorMonto || []).map((a, i) => (
                                        <TableRow key={i} hover>
                                            <TableCell sx={{ fontSize: '0.82rem' }}>{a.area}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem' }}>{fmtNum(a.exp_count)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#c62828' }}>
                                                {fmtMoney(a.monto)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Top deudores */}
            <Paper sx={{ p: 2, borderRadius: `${BR}px` }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: PRIMARY }}>
                    Top 20 deudores por monto demandado
                </Typography>
                <TableContainer sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Demandado</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>DNI / CUIT</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Exp.</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Monto</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data.topDeudores || []).map((d, i) => (
                                <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.78rem' }}>{i + 1}</TableCell>
                                    <TableCell sx={{ fontSize: '0.82rem' }}>{d.demandado || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{d.dni}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.82rem' }}>{fmtNum(d.exp_count)}</TableCell>
                                    <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#c62828' }}>{fmtMoney(d.monto_total)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
